import React, { useEffect, useState } from 'react';
import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import { Autocomplete, Box, Button, FormHelperText, Paper, Stack, TextField, Typography } from '@mui/material';

import { Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';

import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';

import {
  availableProductsCodeAndTypes,
  changeDeliveredProduct,
  getSale,
} from '../../../redux/features/assetManagementSlice';

import { CancelButton, SubmitButton } from '../../../components/mui/Buttons/Buttons';

import { savePaginationData } from '../../../redux/features/globalDatagridSlice';
import { Icon } from '@iconify/react/dist/iconify.js';

const ChangeDeliveredProduct = ({ row, dispatchParams }) => {
  const [open, setOpen] = useState(false);

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { paginationData } = useSelector((state) => state.globalDataGrid);

  const { loading, productSerialData } = useSelector((store) => store.assetmanagement);

  useEffect(() => {
    if (open && row?.subCategoryId) {
      dispatch(availableProductsCodeAndTypes(row?.subCategoryId));
    }
  }, [open, row?.subCategoryId]);

  const formik = useFormik({
    initialValues: {
      purchaseID: '',
      product_id: '',
    },

    validationSchema: Yup.object({
      purchaseID: Yup.number().required('Please Choose Product'),
    }),

    onSubmit: (values) => {
      const payload = {
        saleID: row?.salesID,
        purchaseID: values.purchaseID,
        product_id: values.product_id,
      };

      dispatch(changeDeliveredProduct(payload)).then((res) => {
        if (res.payload.status === 'success') {
          enqueueSnackbar(res.payload.message, {
            variant: 'success',
          });

          dispatch(getSale(dispatchParams));

          dispatch(
            savePaginationData({
              ...paginationData,
              openAnchor: false,
            }),
          );

          handleCancel();
        } else {
          enqueueSnackbar(res.payload.message, {
            variant: 'error',
          });
        }
      });
    },
  });

  const handleProductChange = (event, option) => {
    formik.setFieldValue('purchaseID', option?.purchaseID);

    formik.setFieldValue('product_id', option?.product_id);
  };

  const handleCancel = () => {
    setOpen(false);
    formik.resetForm();
  };

  return (
    <GlobalDialog
      maxWidth={'sm'}
      fullWidth
      title={'Change Delivered Product'}
      open={open}
      setOpen={setOpen}
      closeButton={true}
      closeCondition={handleCancel}
      popupButton={
        <Button
          size="small"
          variant="outlined"
          color="warning"
          disabled={row?.deliveryStatus != 1}
          onClick={() => setOpen(true)}
        >
          Change Delivered Product
        </Button>
      }
      content={
        <Box>
          <FormikProvider value={formik}>
            <Form onSubmit={formik.handleSubmit}>
              <Box>
                <Box component={Paper} elevation={3} p={2} borderRadius={2} sx={{ maxWidth: 400 }}>
                  <Stack spacing={1.5}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Order Details
                    </Typography>

                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Icon icon="icon-park-solid:people" width="22" height="22" style={{ color: '#7e4d8b' }} />
                      <Typography variant="body1">{row.buyerName}</Typography>
                    </Stack>

                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Icon icon="mdi:cart" width="22" height="22" style={{ color: '#7e4d8b' }} />
                      <Typography variant="body1">Item : {row.subCategoryName}</Typography>
                    </Stack>

                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Icon icon="fluent:book-number-16-filled" width="22" height="22" style={{ color: '#7e4d8b' }} />
                      <Typography variant="body1">Quantity: {row.count}</Typography>
                    </Stack>

                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Icon icon="material-symbols:date-range" width="22" height="22" style={{ color: '#7e4d8b' }} />
                      <Typography variant="body1">
                        Requested On: {new Date(row.createdAt).toLocaleDateString()}
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>

                <Box mt={2} component={Paper} elevation={2} p={2} borderRadius={2}>
                  <Typography variant="h6" gutterBottom>
                    Select New Product
                  </Typography>
                  <Autocomplete
                    disabled={loading}
                    name="purchaseID"
                    id="purchaseID"
                    options={productSerialData}
                    getOptionLabel={(option) => option.productSerialCode || option.product_name || ''}
                    value={productSerialData?.find((item) => item.purchaseID == formik.values.purchaseID) || null}
                    onChange={handleProductChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        autoComplete="off"
                        label="Product Code"
                        error={formik.touched.purchaseID && Boolean(formik.errors.purchaseID)}
                        helperText={formik.touched.purchaseID && formik.errors.purchaseID}
                      />
                    )}
                  />
                  {formik.touched.purchaseID && formik.errors.purchaseID && (
                    <FormHelperText error>{formik.errors.purchaseID}</FormHelperText>
                  )}{' '}
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mt: 2,
                }}
              >
                <Stack direction={'row'} spacing={1}>
                  <CancelButton action={handleCancel}>Cancel</CancelButton>

                  <SubmitButton disabled={loading} type="submit">
                    {loading ? 'Updating...' : 'Update Product'}
                  </SubmitButton>
                </Stack>
              </Box>
            </Form>
          </FormikProvider>
        </Box>
      }
    />
  );
};

export default ChangeDeliveredProduct;
